import React from 'react'
import { NavigateFn } from '@reach/router'
import {
  Flex,
  IconButton,
  NewGridTable,
  IGridTableColumn,
  Toaster,
  ColorSquare,
  PrettyNumber,
  ColorSelect,
} from '@ui'
import { Classes } from '@blueprintjs/core'
import { pluralize } from '@utils'
import useSelection, { ISelection } from '@hooks/useSelection'
import { IDepartmentsHook } from './hooks/useDepartments'
import { IDMTableDepartment } from '@api'
import api from '@api'
import { ApolloError } from 'apollo-boost'
import classnames from 'classnames'
import { useReload } from '@hooks/reloadContext'
import { IGridTableCell } from '@ui/NewGridTable'
import track from '@track'
import Event from '@track/Event'
import css from './tableView.module.scss'

export default function TableView({
  navigate,
  hook,
  clearFilter,
}: {
  navigate?: NavigateFn
  hook: IDepartmentsHook
  clearFilter: () => void
}) {
  const { items, totalDepartments, sort, sortBy, nextPage, pageInfo, query, status } = hook
  const departmentSelection = useSelection()

  const columns: IGridTableColumn<IDMTableDepartment>[] = [
    {
      label: 'Department / Team',
      sort: 'name',
      colSpan: 2,
      Cell: ({ skeleton, cell }) => (
        <div
          onClick={() => {
            if (!skeleton) navigate?.(`?departmentId=${cell?.data.id}`)
          }}
          className={classnames(
            'flex items-center group cursor-pointer',
            { 'py-1': skeleton },
            { 'py-1': !skeleton && !cell?.data.totals.teams }
          )}
        >
          <ColorSquare skeleton={skeleton} style={{ background: `#${cell?.data.color}` }} />
          <div
            data-test="department_team"
            className={classnames(
              'ml-2 font-medium text-base group-hover:underline hover:text-link',
              {
                [Classes.SKELETON]: skeleton,
              }
            )}
          >
            {cell?.data.name || 'Departemnt Placeholder'}
          </div>
        </div>
      ),
    },
    {
      label: 'Employees Associated',
      sort: 'employeesCount',
      alignRight: true,
      Cell: ({ skeleton, cell }) => (
        <span
          data-test="employees_associated"
          className={classnames('text-base', { [Classes.SKELETON]: skeleton })}
        >
          <PrettyNumber value={cell?.data.totals.employees || 0} />
        </span>
      ),
    },
    {
      label: 'Spaces Associated',
      sort: 'spacesCount',
      alignRight: true,
      Cell: ({ skeleton, cell }) => (
        <span
          data-test="spaces_associated"
          className={classnames('text-base', { [Classes.SKELETON]: skeleton })}
        >
          <PrettyNumber value={cell?.data.totals.spaces || 0} />
        </span>
      ),
    },
    {
      label: '',
      Cell: ({ skeleton, cell }) => <span />,
    },
  ]

  return (
    <>
      <Toolbar
        totalDepartments={totalDepartments}
        isFiltered={!!query}
        resetFilter={clearFilter}
        isLoading={['loading', 'filtering'].indexOf(status) >= 0}
        selection={departmentSelection}
        items={items}
      />

      <NewGridTable<IDMTableDepartment>
        status={hook.status}
        columns={columns}
        data={items}
        selection={departmentSelection}
        sort={{
          ...sort,
          onSort: sortBy,
        }}
        page={{
          hasNextPage: pageInfo.hasNextPage,
          endCursor: pageInfo.endCursor,
          onNextPage: nextPage,
        }}
        skeletonSize={status === 'loading' ? 10 : Math.min(totalDepartments, 10) || 1}
        track={{
          select: () =>
            track.departments({
              eventName: Event.ROW_SELECT,
            }),
          selectAll: () =>
            track.departments({
              eventName: Event.SELECT_ALL,
            }),
        }}
        headerTop={52}
      />
    </>
  )
}

function Toolbar({
  totalDepartments,
  isFiltered,
  resetFilter,
  isLoading,
  selection,
  items,
}: {
  totalDepartments: number
  isFiltered: boolean
  resetFilter: () => void
  isLoading: boolean
  selection: ISelection
  items: IGridTableCell<IDMTableDepartment>[]
}) {
  const reload = useReload()
  const undo = () => {
    const payload: IDMTableDepartment[] = []

    // Find and save all selected departments and teams
    items.forEach(({ data, children }) => {
      selection.items[data.id] && payload.push({ ...data })
      if (children?.length) {
        children.forEach(({ data }) => selection.items[data.id] && payload.push({ ...data }))
      }
    })

    // TODO: add new mutation
    payload.length &&
      Promise.all(payload.map(({ id, color }) => api.department.update({ id, color })))
        .catch((error) => Toaster.fail({ error }))
        .finally(reload.departments)
  }

  return (
    <Flex className={css.toolbarDepartmentManagement} justify="space-between">
      <Flex align="center" style={{ color: 'var(--grey1)' }}>
        {selection.count > 0 ? (
          <>
            <ColorSelect
              buttonProps={{ text: 'Update Color' }}
              onChange={(color: string) =>
                api.department
                  .updateMany({
                    ids: Object.keys(selection.items),
                    changes: { color },
                  })
                  .then(() => {
                    track.departments({
                      eventName: Event.BATCH_ACTION,
                      props: {
                        actionName: 'Remove workplace groups',
                        actionValue: null,
                        batchCount: selection.count,
                      },
                    })
                    Toaster.success({
                      message: `Color updated for ${selection.count} ${pluralize(
                        selection.count,
                        'Department'
                      )}`,
                      action: {
                        text: 'Undo',
                        onClick: undo,
                      },
                    })
                    reload.departments()
                  })
                  .catch((error: ApolloError) => {
                    Toaster.fail({ error })
                  })
              }
            />

            <span className="ml-2">{selection.count} selected</span>
            <IconButton
              className="ml-1"
              minimal
              iconName="times"
              tooltip="Clear selection"
              onClick={() => {
                selection.clear()
                track.departments({
                  eventName: Event.CLEAR_ALL_FILTERS,
                })
              }}
            />
          </>
        ) : (
          <span className={isLoading ? Classes.SKELETON : ''}>
            {totalDepartments}{' '}
            {isFiltered
              ? pluralize(totalDepartments, 'filtered result')
              : pluralize(totalDepartments, 'department')}
            {isFiltered && (
              <IconButton
                minimal
                style={{ marginLeft: 5 }}
                iconName="times"
                onClick={resetFilter}
                tooltip="Clear Filter"
              />
            )}
          </span>
        )}
      </Flex>
    </Flex>
  )
}
